<template>
  <div class="widget-wrap" :style="{ 'background-color': configs.background || '#000' }">
    <div :class="{ player: true, vertical: !!configs.vertical }">
      <div @click="menuVisible = true" class="menu-trigger">
        <svg @click="menuVisible = true" :fill="configs.start_remaining || '#fff'" stroke-width="0" viewBox="0 0 16 16" height="22px" width="22px" xmlns="http://www.w3.org/2000/svg">
          <path d="M3 9.5a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3m5 0a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3m5 0a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3"></path>
        </svg>
      </div>
      <player-menu @close="menuVisible = false"
                   :podcast="podcast"
                   :shows="configs.shows || []"
                   :api-prefix="apiPrefix"
                   v-if="menuVisible"></player-menu>
      <description-modal
        @close="descriptionVisible = false"
        :description="podcast.description"
        v-if="descriptionVisible"></description-modal>
      <div class="main-image">
        <div class="main-desc">{{podcast.description}}</div>
        <img style="display: none"
             :src="podcastImage"
             @load="imageLoad"
             @error="imageError"
             @loadstart="loadStart"/>
        <img class="image"
             v-if="imageLoaded"
             :src="podcastImage"
             :alt="podcast.name"/>
        <div class="image image-placeholder" v-if="!imageLoaded">
          {{ imageLoading ? 'Loading image...' : 'Image' }}
        </div>
      </div>
      <div class="content">
        <h6 :style="{ color: configs.play_button }">
          {{ podcast.name }}
        </h6>
        <div class="info" :style="{ color: configs.start_remaining || 'gray' }">
          <span class="date">{{ podcast.displayDate }}</span>
          <span>•</span>
          <span class="duration">
            {{ formatDuration(podcast.duration) }}
          </span>
          <span>•</span>
          <span @click="descriptionVisible = true">
            <svg stroke="currentColor" fill="currentColor" stroke-width="0" viewBox="0 0 1024 1024" height="16px" width="16px" xmlns="http://www.w3.org/2000/svg"><path d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm0 820c-205.4 0-372-166.6-372-372s166.6-372 372-372 372 166.6 372 372-166.6 372-372 372z"></path><path d="M464 336a48 48 0 1 0 96 0 48 48 0 1 0-96 0zm72 112h-48c-4.4 0-8 3.6-8 8v272c0 4.4 3.6 8 8 8h48c4.4 0 8-3.6 8-8V456c0-4.4-3.6-8-8-8z"></path></svg>
          </span>
        </div>
        <scrubber
          :is-loading="isLoading"
          :duration="duration"
          :position="seekPos"
          :time-elapsed="timeElapsed"
          :configs="configs"
          :time-remaining="timeRemaining"
          :podcast-id="podcast.id"
          :theme="configs.theme"
          :is-live-stream="isLiveStream"
          @seeking="onSeeking"
        ></scrubber>
        <!-- D-92: `:show-increments` was `false`, which the child IGNORED because the
             prop was declared and never read — so the buttons rendered anyway. Honouring
             the prop as written would have DELETED v4's only skip controls, which
             v4-audio.spec.js clicks and D-40 built coverage for. v3's classic player
             passes `!isLiveStream`; v4 has `isLiveStream` and simply never wired it, so
             this restores the evident intent: visible on demand, hidden for live where
             seeking is meaningless. -->
        <play-pause
          :is-playing="isPlaying"
          :is-loading="isLoading"
          :show-increments="!isLiveStream"
          :configs="configs"
          :theme="configs.theme"
          @play="playMedia"
          @pause="pauseMedia"
          @decrement="decrement"
          @increment="increment"
        ></play-pause>
      </div>
      <audio
        ref="player"
        @timeupdate="onTimeUpdate"
        @canplay="onCanPlay"
        @ended="onEnded"
        @error="onMediaError"
      >
        <source :src="audioSrc" @error="onMediaError" />
        <p>Your browser doesn't support HTML5 audio.</p>
      </audio>
    </div>
    <div class="full" :style="{ color: configs.start_remaining || '#fff' }">
      <div class="header">
        <div>
          <span class="left-up">Next up</span>
        </div>
        <div class="head-right">
          <input type="search"
                 ref="searchInput"
                 :style="{ 'border-color': configs.play_button, opacity: '0.7', color: configs.play_button || '#fff' }"
                 class="search-input"
                 v-model="searchTerm"
                 placeholder="Search..." />
          <span>{{ podcasts.length }} episodes</span>
        </div>
      </div>
      <div class="results">
        <div v-for="episode in filteredPodcasts" :key="episode.id" class="result">
          <div class="left">
            <div class="image-wrap" @click="loadPodcast(episode.id, true)">
              <img class="result-image" :src="episode.image" />
              <span class="pause-icon" v-if="episode.id === podcast.id && isPlaying">
                <svg width="36" height="36" viewBox="0 0 36 36" :fill="configs.play_button || '#000'" xmlns="http://www.w3.org/2000/svg">
                    <path d="M18 0.5C8.33475 0.5 0.5 8.33475 0.5 18C0.5 27.6652 8.33475 35.5 18 35.5C27.6652 35.5 35.5 27.6652
                    35.5 18C35.5 8.33475 27.6652 0.5 18 0.5ZM14.5 25C13.534 25 12.75 24.216 12.75 23.25V12.75C12.75 11.784
                    13.534 11 14.5 11C15.466 11 16.25 11.784 16.25 12.75V23.25C16.25 24.216 15.466 25 14.5 25ZM21.5 25C20.534 25
                    19.75 24.216 19.75 23.25V12.75C19.75 11.784 20.534 11 21.5 11C22.466 11 23.25 11.784 23.25 12.75V23.25C23.25
                    24.216 22.466 25 21.5 25Z" ></path>
                </svg>
              </span>
              <span v-else :class="{'play-icon': true, playing: episode.id === podcast.id }">
                  <svg :fill="configs.play_button || '#000'" stroke-width="0" viewBox="0 0 384 512" height="36px" width="36px" xmlns="http://www.w3.org/2000/svg">
                  <path d="M73 39c-14.8-9.1-33.4-9.4-48.5-.9S0 62.6 0 80V432c0 17.4 9.4 33.4 24.5 41.9s33.7 8.1 48.5-.9L361 297c14.3-8.7 23-24.2 23-41s-8.7-32.2-23-41L73 39z"></path>
                </svg>
              </span>
            </div>
            <div class="result-name" :style="{ color: configs.play_button || '#fff' }">
              <span>{{ episode.name }}</span>
            </div>
          </div>
          <div class="right" :style="{ color: configs.play_button || '#fff', opacity: '0.8' }">
            <div>{{ episode.displayDate }}</div>
            <div>•</div>
            <div>{{ formatDuration(episode.duration) }}</div>
          </div>
      </div>
    </div>
    </div>
  </div>
 </template>

<script>
import Scrubber from './Controls/Scrubber';
import PlayPause from './Controls/PlayPause';
import PlayerMenu from './Controls/Menu.vue';
import DescriptionModal from './Controls/DescriptionModal.vue';
import { formatMonthDay } from '../utils/dateFormat';
import { getJson } from '../utils/http';
import { getLiveStreamUrl, urlGetter } from '../utils/utils';
import { attachLiveStream, detachLiveStream } from '../utils/hls_engine';
import { reportNotAllowedIfApplicable, reportPlayerRetry } from '../core/faro';

// Note: v4's template only renders the four components above plus inline
// HTML. v3 imports PlayerInfo / HashTagModal / Description / Gallery /
// Search; v4 doesn't, so they're not registered here. The byte-identical
// duplicate files under v4-components/ should be deleted in a follow-up
// (REVIEW.md §4 P1) — they're not imported, but they still live in the
// tree and will rot.

export default {
  name: 'v4-player',
  // 'image-loaded' MUST be declared: undeclared, it stays in $attrs and Vue
  // also attaches it as a native addEventListener on the root element. v2's
  // three components all declare it; v4 was the only one that did not.
  emits: ['switch-category', 'image-loaded'],
  components: {
    DescriptionModal,
    PlayerMenu,
    Scrubber,
    PlayPause,
  },
  props: {
    podcasts: Array,
    configs: Object,
    apiResourceUrl: String,
    apiPrefix: String,
    categories: Array,
  },
  data() {
    return {
      imageLoading: false,
      imageLoaded: null,
      podcast: this.podcasts[0],
      isPlaying: false,
      menuVisible: false,
      descriptionVisible: false,
      seekPos: 0,
      duration: 0,
      timeElapsed: 0,
      timeRemaining: 0,
      mediaReady: false,
      // The failure latch v2 has had since D-133, and its outcome companion.
      // `mediaFailed` says "the load failed"; `retryPending` says "the listener asked
      // for another go and we do not yet know how it went", which is what turns a
      // retry COUNT into a success RATE.
      mediaFailed: false,
      retryPending: false,
      canPlayLiveStream: false,
      liveStreamUri: '',
      audioIsLoaded: false,
      hashTagModalIsVisible: false,
      searchIsVisible: false,
      searchTerm: '',
      showingFilteredPodcasts: false,
      isLoading: false,
      autoplayActive: true,
      dropToLive: false,
      isLiveStream: false,
      preloadedAudio: [], // podcast ids of audio files already loaded
      showTitle: '',
    };
  },
  computed: {
    m4aURI() {
      return `${this.apiPrefix}${this.podcast.m4aFile}`;
    },

    audioSrc() {
      if (this.canPlayLiveStream) {
        return this.liveStreamUri;
      }

      return this.m4aURI;
    },

    shouldRenderAutoPlaySettings() {
      return ['next', 'live'].includes(this.configs.autoplay);
    },

    hasAutoplay() {
      // PS-5: pure — the `this.dropToLive = true` side effect moved to onEnded()
      // (a method). v4 never reads hasAutoplay during render, so on the base code
      // dropToLive was armed lazily on the first onEnded; onEnded now reproduces
      // that exact timing.
      return (
        ['next', 'live'].includes(this.configs.autoplay) && this.autoplayActive
      );
    },

    filteredPodcasts() {
      let filtered = [];
      const searchTerm = this.searchTerm.toLowerCase().trim().replace('#', '');

      if (!searchTerm) {
        filtered = this.podcasts;
      } else {
        filtered = [...this.podcasts].filter((podcast) => {
          const { name = '', categories = [], description = '' } = podcast;

          // Matches name or description
          const matchesName = name.toLowerCase().includes(searchTerm);
          const matchesDescription = description
            .toLowerCase()
            .includes(searchTerm);
          if (matchesName || matchesDescription) {
            return true;
          }
          // Matches category
          let matchesCategory;
          for (let index = 0; index < categories.length; index++) {
            const category = categories[index];
            if (!category) continue;
            if (category.name.toLowerCase().includes(searchTerm)) {
              matchesCategory = true;
              break;
            }
          }
          return matchesCategory;
        });
      }

      // add input text search filter
      filtered = filtered.filter(p => {
        const { name = '', description = '' } = p;
        const matchesName = name.toLowerCase().includes(searchTerm);
        const matchesDescription = description.toLowerCase().includes(searchTerm);
        return matchesName || matchesDescription;
      });

      // add display date
      filtered = filtered.map(p => {
        // eslint-disable-next-line no-param-reassign
        p.displayDate = formatMonthDay(p.timestampCreated * 1000);
        return p;
      });

      return filtered;
    },

    themeStyles() {
      return {
        background: this.configs.theme.background,
      };
    },

    resourceUrlForShowName() {
      if (!this.apiResourceUrl) return '';
      const url = new URL(this.apiResourceUrl);
      const id = this.podcast.id;
      url.search = `?limit=1&page=1&id=${id}`;
      return url.toString();
    },

    podcastImage() {
      let img;
      if (this.podcast.squareImage) {
        img = urlGetter(this.apiPrefix, this.podcast.squareImage);
      } else if (this.podcast.shareImage) {
        img = urlGetter(this.apiPrefix, this.podcast.shareImage);
      } else if (this.podcast.originalImage) {
        img = urlGetter(this.apiPrefix, this.podcast.originalImage);
      } else {
        img = urlGetter(this.apiPrefix, this.podcast.image);
      }
      return img;
    },

    displayDate() {
      return this.podcast.widgetPublishDate || this.podcast.timestampCreated;
    },
  },
  methods: {
    loadStart() {
      this.imageLoading = true; // Image loading has started
      this.imageLoaded = false;
    },
    formatDuration(seconds) {
      if (seconds <= 60) {
        const roundedSeconds = Math.round(seconds);
        return `${roundedSeconds} sec${roundedSeconds !== 1 ? 's' : ''}`;
      }
      const minutes = Math.round(seconds / 60);
      return `${minutes} min${minutes !== 1 ? 's' : ''}`;
    },
    toggleMenu(value) {
      this.menuVisible = value;
    },
    imageError() {
      this.imageLoading = false;
      this.imageLoaded = false;
      // The error path swaps the placeholder text too ('Loading image…' → 'Image'),
      // so the box can move here as well. Cheap to cover: informResize is
      // debounced and drops a duplicate message.
      this.notifyShellOfResize();
    },

    // Tell the shell the cover changed the layout, so it re-posts the height to
    // the embedder. R5's mobile stack made `.main-image` auto-height, which means
    // the cover is a ~19px collapsed box until the image lands and 240px after —
    // and `informResize`'s 200ms debounce had already posted the PRE-cover height.
    // Without this the iframe stays 240px short of its content with
    // `scrolling="no"`, putting the whole episode list below the fold: measured
    // 221px clipped and unreachable on the deployed build at a 390px viewport
    // (#399 / PRS §6.63). v1 has always done this — podcast.vue → podcast_list →
    // app.imageLoaded — and v4 simply never did.
    // $nextTick so the placeholder→image swap has actually rendered; measuring
    // before it would just re-post the height we already sent.
    notifyShellOfResize() {
      this.$nextTick(() => this.$emit('image-loaded'));
    },
    imageLoad() {
      this.imageLoading = false;
      this.imageLoaded = true;
      this.notifyShellOfResize();
    },
    playMedia() {
      // 🔑 A tap after a failed load is a RETRY, not a play. `play()` on a dead
      // element rejects and changes nothing, so without this branch the control
      // looks alive and fails identically every time — harder to diagnose than v2's
      // old inertness, not easier.
      if (this.mediaFailed) {
        this.mediaFailed = false;
        this.retryPending = true;
        this.reportRetry('attempted');
        // The LIVE path starts playback itself, inside the engine — nothing further
        // to do here.
        if (this.retryLoad()) return;
        // The m4a path has only re-pointed and re-loaded the element, so it FALLS
        // THROUGH to the ordinary play below rather than stopping at the reload.
        //
        // 🔑 That is not a convenience. A tap on the play control is a request for
        // AUDIO, and a retry that reloads and then sits there makes the listener tap
        // twice for no reason they can see — the live branch above would already have
        // played. Falling through also means the retry reuses the rejection handling,
        // the `isLoading` bookkeeping and the NotAllowed reporting that the ordinary
        // path already has, instead of growing a second copy of all three.
        //
        // 🪤 MEASURED, and it is why this shape exists at all: with the retry
        // returning here, `autoplay-rejection.spec.js` (D-207) went RED in four
        // places. `stubAudio`'s fixture genuinely fails to decode in chromium, so
        // `mediaFailed` is already true when the spec clicks — the click became a
        // retry, `play()` was never called, and the denial that spec exists to
        // measure never happened. The probe that settled it recorded the pre-click
        // error as `{tag:'SOURCE', hasSrcAttr:true}`, which also refuted my first
        // hypothesis (a src-less `<source>`, the guard v2 carries) before I shipped
        // a fix for the wrong cause.
      }
      if (!this.preloadedAudio.includes(this.podcast.id)) {
        this.isLoading = true;
      }
      if (this.canPlayLiveStream && !this.isPlaying) {
        // if live stream paused and play is clicked, original playlist should start again
        this.canPlayLiveStream = false;
        this.isLiveStream = false;
        const forceUpdate = true;
        this.loadPodcast(this.podcast.id, forceUpdate);
      }

      if (!this.isPlaying) {
        const player = this.$refs.player;
        const playPromise = player.play();

        // Backwards compatibility for autoplay
        if (playPromise !== undefined) {
          // 🔴 D-203/D-207 — thenable-safe, WITHOUT collapsing to `.then(f, h)`.
          //
          // This read `.catch` off whatever `play()` returned. `play()` may return a
          // host-patched thenable with only `.then` (D-176: the host page is a
          // customer's site and its scripts are not ours), and reading `.catch` off
          // one throws a TypeError SYNCHRONOUSLY inside the click handler, at the
          // moment playback has already failed — so the listener gets a broken
          // transport instead of the repair below.
          //
          // The chained form is kept deliberately: in `.then(f).catch(h)` the handler
          // also catches a throw from `f`, and collapsing to `.then(f, h)` silently
          // drops that. Wrapping FIRST makes the rest of the chain native, so both
          // properties hold. 🪤 `playPromise.then(f).then(null, h)` does NOT fix it —
          // on a minimal thenable `T.then(f)` returns whatever that `then` returns,
          // possibly `undefined`, and the next link throws the same TypeError.
          //
          // The `!== undefined` guard above is kept for genuinely ancient browsers
          // whose `play()` returns nothing: `Promise.resolve(undefined)` FULFILS, so
          // without the guard the "play back successful" handler would run for them.
          Promise.resolve(playPromise)
            .then(() => {
              // play back successful
            })
            .then(null, (error) => {
              console.error(error);
              // F-3/MR-69 review fold: this .catch() is exactly why a global
              // unhandledrejection listener in faro.js never fires — count it
              // here instead, at the actual call site.
              reportNotAllowedIfApplicable(error, this.podcast && this.podcast.station);
              // D-131 — clear the load state on a REJECTED play, or the spinner is
              // permanent. `playMedia` raises `isLoading` for a non-preloaded episode;
              // if `play()` then rejects, the element dispatches NO new `error`
              // (NotSupportedError on an already-errored element, NotAllowedError on a
              // blocked autoplay), so neither `onMediaError` nor `onCanPlay` can ever
              // run and nothing else clears it.
              //
              // 🪤 This is what made D-105's fix a ONE-SHOT: it restored the play
              // button, and the first click on that button re-wedged the spinner for
              // good — measured in chromium, loaders 0 -> 1 with no new error event.
              // The widget was advertising the control that reached the dead end.
              // 🔴 D-137 — an AbortError here is BENIGN and must NOT be treated as a
              // media failure. `loadPodcast -> pauseMedia() -> player.pause()` rejects
              // the still-pending `play()` on EVERY episode switch made while the
              // previous episode is buffering, and D-131 routed that into
              // `onMediaError()` — clearing `isPlaying`/`isLoading`/`mediaReady` for the
              // episode the listener ABANDONED, which corrupts the state of the one now
              // playing.
              //
              // MEASURED end-to-end in chromium against the widget's own UI: audio
              // `paused:false, currentTime advancing, readyState 4` while the transport
              // read **"Play"**. Neither Player binds the native `play` event, so
              // nothing repairs `isPlaying` — the wrong value LATCHES until the next
              // user action and pausing then takes two taps.
              //
              // 🪤 The repo already documents this rejection as routinely benign in the
              // two places it deliberately swallows it (podcast.vue's `onSeeking`:
              // "pause is interrupting the play at times"; v3 `playLiveStream`:
              // 'Play call interrupted'). D-131 read the same event as a failure.
              if (error && error.name === 'AbortError') return;
              this.onMediaError();
            });
        }
      }
      this.isPlaying = true;
    },

    /**
     * Pause this media in particular.
     */
    pauseMedia() {
      if (this.$refs.player) {
        this.$refs.player.pause();
      }

      this.isPlaying = false;
    },

    /**
     * Updates the audio players if a new podcast ID is provided then plays the media
     *
     * @param {Number} id the podcast id
     * @param {Boolean} forceUpdate force update the audio element source, default is false
     * @return {void}
     */
    loadPodcast(id, forceUpdate = false) {
      if (id === this.podcast.id && this.isPlaying) {
        this.pauseMedia();
        return;
      } else if (id === this.podcast.id && !this.isPlaying) {
        this.playMedia();
        return;
      }

      this.pauseMedia();
      // 🪤 THE D-138 SHAPE, pre-empted. A latch armed once and never cleared stays
      // armed for the life of the component: the next episode would render as
      // failed and its FIRST tap would be misrouted into the retry branch. Cleared
      // here — the episode-switch entry point — and NOT in `resetTimeline()`, which
      // `updatePlayer()` also calls from inside the retry itself and would therefore
      // cancel the very retry it is meant to be tracking.
      this.mediaFailed = false;
      this.retryPending = false;
      if ((id && id !== this.podcast.id) || forceUpdate) {
        this.podcast = this.podcasts.find((podcast) => podcast.id === id);
        this.updatePlayer();
      }
      this.setShowTitle();
      this.playMedia();
      // v4 renders no <player-info> child, so $refs.info is undefined; guard the
      // dead reach-through (previously threw TypeError on every episode-switch).
      if (this.$refs.info) {
        this.$refs.info.scrollTitle();
      }
    },

    updatePlayer() {
      const audioPlayer = this.$refs.player;
      audioPlayer.removeAttribute('src');

      // D-103 — the guard that used to wrap this block was DEAD: `removeAttribute`
      // one line above leaves the reflected `src` IDL attribute empty, so its first
      // clause `!audioPlayer.src` was unconditionally true and the live-vs-m4a
      // clause could never be evaluated. VERIFIED in chromium, not assumed:
      // setAttribute -> 'https://host/media/x.m4a', removeAttribute -> '' (falsy).
      //
      // Deleted rather than rewritten. No commit or comment records what the
      // live-vs-m4a branch was FOR, and reconstructing an intent from a condition
      // that has never once evaluated would be fiction. Deleting is provably
      // behaviour-preserving; guessing is not. Pinned by
      // test/unit-vue/update-player-dead-condition.spec.js, which also pins the
      // PREMISE (src is falsy after removeAttribute) so the deletion's own
      // justification fails loudly if an engine ever changes it.
      // Detach the live engine (hls.js, LP-H/MR-75). detachLiveStream is
      // idempotent + throw-safe (mirrors the beforeUnmount teardown +
      // setupHlsEngine), so the src-swap below still runs regardless of
      // whether a live session was attached. (Was the guarded Viblast stop
      // call on the runtime global, migrated to hls.js.)
      detachLiveStream(audioPlayer);
      audioPlayer.setAttribute('src', this.audioSrc);
      audioPlayer.load();
      // load() puts the element back at readyState 0 with duration NaN, so every
      // Vue-data mirror of its timeline is now a lie and the controls cannot be
      // trusted until `canplay`. INSIDE the branch on purpose: where the src is
      // not swapped the element keeps its state and the mirrors stay valid
      // (D-97/D-98).
      this.resetTimeline();
    },

    /**
     * Forget everything mirrored from the media element and re-open the load
     * window. Called only where the element is actually reloaded.
     *
     * `isLoading` is set here rather than left to `playMedia`, which sets it only
     * `if (!this.preloadedAudio.includes(this.podcast.id))` — a cache that cannot
     * vouch for an element reset a line earlier. That hole kept the load window
     * fully reachable for any RE-selected episode: no loader, live controls, and an
     * element at readyState 0 (D-98).
     *
     * `mediaReady` has no readers today, but it is written by `onCanPlay` and was
     * never cleared, so it is reset with the rest rather than left as a third
     * variant of the same defect (see D-94 on `duration`).
     *
     * @return {void}
     */
    resetTimeline() {
      this.duration = 0;
      this.timeElapsed = 0;
      // NOT zero — this mirror is the one thing here that IS knowable at
      // readyState 0. `mounted()` seeds it from parseInt(podcast.duration), the
      // playlist metadata, and `loadPodcast` sets `this.podcast` to the newly
      // chosen episode BEFORE calling updatePlayer() — so the right answer is
      // already in hand. Zeroing it made the scrubber advertise every freshly
      // selected episode as 00:00 long for the whole window this reset opens.
      // Caught by review, not by a gate — and the reason is NOT masking, which is
      // what an earlier version of this comment claimed. MEASURED across the 162
      // goldens: 60 carry NO mask at all, 84 carry one rect, 18 carry two. The real
      // reason is that no captured state is INSIDE the load window — the `play` cells
      // screenshot after `canplay` — so the visual suite never renders this label
      // while it is wrong, mask or no mask.
      this.timeRemaining = this.metadataDuration();
      this.seekPos = 0;
      this.mediaReady = false;
      this.isLoading = true;
    },

    playLiveStream() {
      this.isLiveStream = true;
      getLiveStreamUrl(this.podcast.station)
        .then((url) => {
          this.liveStreamUri = url;
          this.canPlayLiveStream = true;
          // 🔴 D-153 — guard the ref. `attachLiveStream` already returns early on a
          // falsy element (the documented MED-5 fix, added for exactly this race) and
          // then the very next line dereferenced the same ref. Every caller runs
          // inside a `getLiveStreamUrl(...).then(...)`, so an unmount while that fetch
          // is in flight leaves `$refs.player` cleared: `.play()` threw a TypeError
          // inside the `.then`, the chained OUTER `.catch` swallowed it, and it logged
          // 'Unable to fetch live stream' — for a fetch that SUCCEEDED. An operator
          // reads that and goes hunting a CDN that answered fine.
          //
          // Returning here also stops the writes below from landing on a dead
          // component, which is what MED-5 established as the right handling for an
          // unmount race: it is not an engine failure and must not be reported as one.
          // 🔑 The population is DERIVED, not typed: this guard's own wording ("Every
          // caller…") was correct while the fix reached only 2 of the 3 callers —
          // v1 `podcast.vue` was missed and a two-member `describe.each` could not
          // see it. `test/unit-vue/live-fetch-unmount-population.spec.js` now derives
          // the set from src/ and requires every member to be classified.
          const player = this.$refs.player;
          if (!player) return;
          // 🔴 D-147 — recorded BEFORE the attach, and the ORDER is the entire fix.
          //
          // `notifyUnrecoverable` dispatches a real `error` event and `dispatchEvent`
          // is SYNCHRONOUS, so on the two branches D-143 added (`no_engine`,
          // `attach_threw`) the give-up runs to completion inside `setupHlsEngine()`
          // below — `@error` -> `onMediaError` -> `isPlaying = false`. With this
          // assignment sitting after that call, it overwrote the repair two
          // statements later and the transport went back to reading PAUSE with
          // nothing playing. Nothing else could fix it: no native `play` binding, and
          // `onCanPlay` cannot fire when nothing was attached. That is D-142's defect,
          // reopened on the exact path D-143 exists to signal.
          //
          // 🪤 WHICH ENGINES — measured, because an earlier revision of this comment
          // said flatly "`no_engine` is the SHIPPED off-master state (!207)", and that
          // names the wrong branch for the majority engine. `canPlayType` for the HLS
          // MIME, measured in this repo's own playwright browsers: chromium -> 'maybe'
          // (so it takes the NATIVE branch and `no_engine` is UNREACHABLE there),
          // firefox -> '' (so it takes `no_engine`). Off-master the dead-engine path is
          // therefore `no_engine` on FIREFOX; on Chrome the failure arrives as an
          // ASYNCHRONOUS native `error`, which lands after this assignment and so was
          // never clobbered by the old order. `hls_engine.js:553-560` has recorded this
          // since 2026-08-06 — it is the correction PS-22/PS-23 needed, and the comment
          // it corrects is the one I had just written.
          //
          // 🪤 The comment that used to sit at the old position argued this assignment
          // was safe because "the rejection settles a microtask LATER, so onMediaError
          // above already overwrites this". That is true of the `play()` rejection and
          // FALSE of the engine notify, which lands first and synchronously. A
          // justification that only considered one of the two writers.
          //
          // Nothing observable changes on the healthy path: `play()` is async, so this
          // flag always led actual playback regardless of which side of the attach it
          // sat on.
          this.isPlaying = true;
          this.setupHlsEngine();
          player
            .play()
            // eslint-disable-next-line no-console
            // 🔴 D-176 — `.then(null, handler)`, not `.catch(handler)`: a host page that patches
            // `play()` (ad blockers, some CMPs) can return a THENABLE with no `.catch`, and
            // calling it throws TypeError at the moment playback already failed. `.then` is the
            // only method a thenable must have. Same contract as startPlayback in hls_engine.js.
            .then(null, (err) => {
              console.warn('Play call interrupted', err);
              // F-3/MR-69 review fold: see the playMedia() catch above.
              reportNotAllowedIfApplicable(err, this.podcast && this.podcast.station);
              // 🔴 D-142 — repair the state, don't just log it. This is the OTHER
              // `play()` call site: D-131/D-137 fixed `playMedia` and this one kept
              // the defect. `this.isPlaying = true` below runs unconditionally, so a
              // rejected live play left the transport rendering PAUSE with nothing
              // playing — and neither Player binds the native `play` event, so the
              // wrong value LATCHES until the listener taps the control, which then
              // pauses media that never started.
              //
              // 🪤 The stuck SPINNER that the on-demand side suffers is NOT the main
              // harm here, and the first write-up of this finding had that wrong.
              // `playLiveStream` never raises `isLoading`, there is no live button in
              // the template, and its one caller — `onEnded` under autoplay=live —
              // runs after `onCanPlay` has already cleared it. `isLoading` is stuck
              // only in the select-during-live-fetch race (episode B raises the
              // spinner via `resetTimeline` while `getLiveStreamUrl` is still in
              // flight). `onMediaError` covers that too, so both are closed here.
              //
              // ⚠️ AND ON v4 SPECIFICALLY, none of it is reachable today: `app.vue`
              // initialises `v4PlayerConfigs.autoplay` to 'next' and never reassigns
              // it, while `dropToLive` flips only on `configs.autoplay === 'live'` —
              // so `playLiveStream` has no live caller here at all. This half is
              // LATENT, kept in parity with v3 (where the wire is real, via
              // `queryParams.autoplay`) so that wiring v4's drop-to-live up cannot
              // silently reintroduce the defect. v3's copy of this comment describes
              // a path that actually ships; this one does not.
              //
              // AbortError is excluded for exactly D-137's reason, reached by the
              // other site: `pauseMedia()` rejects a still-pending live `play()`
              // whenever an episode is chosen while the live stream is starting, and
              // by the time that rejection lands the state describes the episode now
              // playing, not the abandoned live attempt.
              if (err && err.name === 'AbortError') return;
              this.onMediaError();
            });
        })
        // eslint-disable-next-line no-console
        .catch((e) => {
          this.isLiveStream = false;
          console.warn('Unable to fetch live stream', e);
        });
    },

    // 🔑 THE RETRY TAKES THE SAME BRANCH THE SETUP TOOK, and reuses the setup paths
    // that already exist rather than growing a third way to point an element at a
    // source. `updatePlayer()` is the m4a path (detach, set src, load, reset the
    // timeline) and `setupHlsEngine()` is the live one; a retry that re-implemented
    // either would be free to drift from it, which is exactly how v1 and v2 ended up
    // with OPPOSITE recovery behaviour on opposite paths — v1's HLS retry worked and
    // its m4a retry did not, v2's m4a retry worked and its HLS retry did not, and
    // neither component had a rule. Both had whatever fell out of the bookkeeping.
    //
    // 🔑 THE DISCRIMINATOR IS `canPlayLiveStream`, NOT v2's `useM4a`. `audioSrc` here
    // is `canPlayLiveStream ? liveStreamUri : m4aURI` — v3/v4 episodes are ALWAYS
    // m4a and only the live stream is HLS. v2's gate is `useM4a` because a default v2
    // embed serves on-demand episodes as `.m3u8` through hls.js. Copying v2's gate
    // would have been a fix shaped like the last one instead of like this component.
    // Returns TRUE when it has fully handled the retry (the live path, which starts
    // playback itself) and FALSE when the caller must still play (the m4a path).
    retryLoad() {
      const player = this.$refs.player;
      if (!player) return true;
      if (this.canPlayLiveStream) {
        // Stop-before-(re)attach, as every other attach site here does.
        detachLiveStream(player);
        // 🔑 `autoplay: true`, and ONLY on the retry. `setupHlsEngine()` passes
        // false because its caller plays explicitly right after; this path is a
        // click on the play control, so the listener has already asked for audio and
        // a re-attach that left silence would make them tap again for no visible
        // reason. A rejected `play()` is caught and reported inside the engine
        // (D-151/D-207), so the optimistic call cannot surface as an unhandled
        // rejection.
        try {
          attachLiveStream(player, this.audioSrc, { autoplay: true });
        } catch (e) {
          // Parity with setupHlsEngine's belt-and-braces catch; the engine is
          // already throw-safe.
        }
        return true;
      }
      // m4a: re-point and re-run resource selection. `updatePlayer()` ends in
      // `removeAttribute`/`setAttribute`/`load()`, which is precisely what an element
      // sitting in error state needs — and unlike a bare `play()` it actually issues
      // a request.
      this.updatePlayer();
      return false;
    },

    // Reported through the SHARED reporter in `core/faro.js`: v2, v3 and v4 all emit
    // this event, and three inline copies of an attribute set is where a schema
    // drifts. `reportNotAllowedIfApplicable` — which shipped v4-ONLY and was the
    // ninth one-of-N defect here — is why that file is where a cross-version
    // reporter lives.
    reportRetry(phase) {
      reportPlayerRetry(phase, {
        playerVersion: 'v4',
        sourceKind: this.canPlayLiveStream ? 'hls' : 'm4a',
        live: this.canPlayLiveStream,
      });
    },

    // Settle a pending retry exactly once, whichever way it went.
    settleRetry(phase) {
      if (!this.retryPending) return;
      this.retryPending = false;
      this.reportRetry(phase);
    },

    setupHlsEngine() {
      // Attach the live HLS stream via the shared hls.js engine (LP-H/MR-75,
      // replacing the old Viblast stop()+setup() calls). autoplay:false keeps
      // the Viblast contract — playLiveStream calls this.$refs.player.play()
      // explicitly right after. attachLiveStream is already throw-safe (it
      // swallows the iOS quirk Viblast threw on); the outer try/catch is kept
      // for belt-and-suspenders parity with the original.
      try {
        attachLiveStream(this.$refs.player, this.audioSrc, { autoplay: false });
      } catch (e) {
        // Defensive — attachLiveStream swallows internally; see its JSDoc.
      }
    },

    /**
     * The episode length as the PLAYLIST reports it, which is knowable before the
     * element has any metadata of its own. `|| 0` because a missing or
     * non-numeric duration is a real condition in this data and parseInt would
     * otherwise put NaN straight into a rendered time label.
     *
     * @return {number} seconds, or 0
     */
    metadataDuration() {
      return parseInt(this.podcast.duration, 10) || 0;
    },

    onTimeUpdate(event) {
      const currentTime = parseInt(event.target.currentTime, 10);
      const duration = parseInt(event.target.duration, 10);

      // Update the slider value to either new seek time or currentTime
      this.seekPos = (100 / (duration || 1)) * currentTime;
      this.timeElapsed = currentTime;
      // 🪤 `load()` QUEUES a timeupdate that fires at readyState 0 with duration
      // NaN, and it lands ONE TICK AFTER resetTimeline()'s re-seed — so without
      // this fallback that event overwrote the re-seed with (NaN || 0) - 0 = 0 and
      // the whole re-seed was inert. MEASURED in three real engines: chromium
      // fires it, webkit and firefox do not. So the bug reproduced only in Blink,
      // the engine most listeners use, and happy-dom cannot see it at all — it
      // queues no timeupdate on load() (D-104).
      // Fall back to the episode metadata, the same source resetTimeline seeds
      // from. A live stream reports Infinity and lands here too; its label is
      // hidden (`v-if="showTime && !isLiveStream"`), and seekPos/timeElapsed above
      // keep updating either way.
      this.timeRemaining = Number.isFinite(event.target.duration)
        ? duration - currentTime
        : this.metadataDuration();
    },

    onCanPlay(event) {
      this.duration = event.target.duration;
      this.mediaReady = true;
      this.isLoading = false;
      this.preloadedAudio.push(this.podcast.id);
      this.settleRetry('recovered');
    },

    /**
     * D-105 — a failed load must return the play button, not spin forever.
     *
     * `isLoading` was cleared in exactly one place, `onCanPlay`, so any media that
     * never reaches `canplay` (404, CORS, decode failure) left the Loader sitting
     * where the play button belongs for the rest of the pageview.
     *
     * 🪤 Bound on BOTH the media element and the `<source>` child, and that is not
     * belt-and-braces. MEASURED in chromium: `setAttribute('src', bad)` — the
     * `updatePlayer()` episode-swap path — fires `error` at the MEDIA element with
     * `el.error.code = 4`, while a failing `<source>` child — what the template
     * renders on FIRST load — fires `error` at the SOURCE, which does not bubble,
     * leaving `el.error` null. Binding only `<audio>` would have fixed the swap
     * and left first load, the commoner failure, spinning.
     *
     * Takes no argument on purpose: `event.target.error` is null on the source
     * path, so reading it would work in testing and be useless in production.
     */
    /**
     * 🔑 D-160 — `isLiveStream` and `canPlayLiveStream` are deliberately NOT cleared
     * here, and that is a MEASURED decision rather than the omission it looks like.
     *
     * They were part of the derived population D-160 raised ("what state means
     * LIVE-IN-PROGRESS, and does the give-up clear it?"), so clearing them looks like
     * the obvious completion of the fix. Measured consequence of doing it, with the
     * live state established and `onMediaError()` called:
     *
     *   audioSrc BEFORE  https://edge.streamon.fm/hls/TEST.m3u8
     *   audioSrc AFTER   https://api.example/a.m4a
     *
     * `audioSrc` is `canPlayLiveStream ? liveStreamUri : m4aURI`, so clearing the flag
     * rewrites `<source :src="audioSrc">` — silently moving the listener off live and
     * back onto the episode that just finished. That is a CONTENT change on every
     * media error, and `@error` is bound on both the element and the `<source>`, so
     * transient failures reach it too.
     *
     * The exit already exists and is deliberate: `playMedia` clears both flags and
     * calls `loadPodcast` when the listener presses Play on a paused live stream. So
     * the post-give-up state here is a usable control (pinned by
     * `test/e2e/live-giveup-recovers-control.spec.js`) beside live chrome that stays
     * until the next tap — cosmetic and self-repairing, unlike v2's permanent label
     * lie, which is the half D-160 actually fixed.
     *
     * ⚠️ The whole unit suite passes with those two lines ADDED, so
     * nothing gates this either way. Recorded here because the next reader will have
     * the same idea and the measurement is not cheap to redo.
     */
    onMediaError() {
      this.isLoading = false;
      this.mediaReady = false;
      this.isPlaying = false;
      // 🔴 v2 has had this latch since D-133; v3/v4 never got one. Without it a
      // failed load is indistinguishable from "not loaded yet", so the next tap
      // takes the ordinary play path and `play()` rejects on an element that is
      // either in error state (m4a) or has had its engine torn down (live) — every
      // time, silently, for the rest of the pageview. The three clears above are
      // what the give-up used to be, in full.
      this.mediaFailed = true;
      // If this failure followed a retry, it IS that retry's outcome. Reported last,
      // after the state above is settled, so a throw in telemetry could never cost
      // the component its give-up handling.
      this.settleRetry('failed');
    },

    onEnded() {
      this.pauseMedia();

      if (this.dropToLive) {
        this.playLiveStream();
      } else {
        // PS-5: reproduce the read-time side effect the hasAutoplay computed used
        // to perform. Evaluating hasAutoplay used to arm dropToLive whenever
        // autoplay === 'live'; that happened here (v4's only hasAutoplay read), so
        // the FIRST onEnded still fell through to the autoplay-next path and
        // dropToLive only took effect on the NEXT ended event. Setting it before
        // the hasAutoplay check keeps that exact timing (hasAutoplay is now pure).
        if (this.configs.autoplay === 'live') {
          this.dropToLive = true;
        }
        let advanced = false;
        if (this.hasAutoplay) {
          const currentPodIndex = this.filteredPodcasts.findIndex(
            (x) => x.id === this.podcast.id
          );
          const nextPod = this.filteredPodcasts[currentPodIndex + 1];

          if (nextPod && nextPod.id) {
            this.loadPodcast(nextPod.id);
            advanced = true;
          }
        }
        // 🔴 `next=continue` — v4's drop-to-live was DEAD CONFIG at two independent
        // breaks, and this closes the second of them. `onEnded` never read
        // `configs.dropToLive` at all: v4's internal `dropToLive` flipped only on
        // `configs.autoplay === 'live'`, and `app.vue` pinned
        // `v4PlayerConfigs.autoplay` to 'next' and never reassigned it — so both
        // routes into the live path were closed. (The other break, the config never
        // being wired at all, is fixed in `app.vue` alongside this.)
        //
        // 🔑 `next === 'continue'` is the wire v1 and v2 ALREADY honour —
        // `parseConfig.js:92` derives `dropToLive` from it — so v4 joins the existing
        // contract instead of inventing a fifth spelling. v3 remains the odd one out:
        // `app.vue` sets `v3PlayerConfigs.dropToLive` while v3 reads
        // `configs.autoplay === 'live'`, leaving its config key inert. Not touched
        // here; changing v3's live trigger is a separate decision.
        //
        // ⚠️ Gated on `!advanced`, i.e. the playlist is EXHAUSTED. v1 has no advance
        // path, so for it "continue" can only mean the next `ended`; v4 does advance,
        // so an ungated rule would truncate the playlist on every episode but the
        // last — the opposite of "continue". Advance first, go live when there is
        // nothing left, which is what the plan describes.
        //
        // 🪤 Deliberately NOT also gated on `autoplayActive`. That looks right by
        // symmetry, but v4 never binds `@toggle-autoplay` in its template (only v3
        // does), so `toggleAutoplay` is unreachable and `autoplayActive` is always
        // true. A condition that cannot be false is the D-92 shape.
        if (!advanced && this.configs.dropToLive) {
          this.dropToLive = true;
          this.playLiveStream();
        }
      }
    },

    /**
     * Event emitted when seeking through audio.
     * @param pos
     */
    onSeeking(pos) {
      const player = this.$refs.player;
      // duration is NaN until metadata arrives and Infinity on a live stream, and
      // currentTime is a WebIDL *restricted* double — a non-finite assignment is a
      // TypeError, not a clamp. The scrubber is enabled through that whole window,
      // so drop the seek rather than throw out of the event handler (D-93).
      if (!Number.isFinite(player.duration)) return;
      const seekTime = player.duration * (pos / 100);
      player.currentTime = seekTime;
    },

    /**
     * Decrements the audio player
     *
     * @param {number} value the amount of seconds to decrement
     * @return {void}
     */
    decrement(value) {
      // Reset to 0 if the decrement value is greater than the current time
      const player = this.$refs.player;
      if (player.currentTime <= value) {
        player.currentTime = 0;
      } else {
        player.currentTime -= value;
      }
    },

    /**
     * Increments the audio player
     *
     * @param {number} value the amount of seconds to increment
     * @return {void}
     */
    increment(value) {
      // Set the currentTime to the duration if the episode would end in <= 30 seconds
      const player = this.$refs.player;
      // D-92: read the ELEMENT's duration, not the `this.duration` data cache —
      // the same fix D-87 applied to v3, on byte-identical code. The cache is
      // written ONLY by onCanPlay and is never reset when the source changes, so
      // between `loadPodcast` -> `updatePlayer()` (removeAttribute('src') /
      // setAttribute('src') / load(), which resets the element to currentTime 0
      // and duration NaN) and the next `canplay`, it still holds the PREVIOUS
      // episode's length. `onSeeking` and `onTimeUpdate` already read the element;
      // `increment` was the only seek decision consulting the cache.
      const duration = player.duration;
      // ⚠️ In the load window a skip must do NOTHING. Advancing relatively does
      // NOT clamp: there is no seekable range at readyState HAVE_NOTHING, so
      // `currentTime += value` accumulates into the default-playback-start-
      // position. MEASURED in chromium AND webkit while fixing v3 — three taps
      // gave 30, 60, 90, which can land past the end and fire `ended`, sending
      // autoplay-next off the episode the listener just chose. Returning early is
      // bounded and idempotent.
      if (!Number.isFinite(duration)) return;
      if (duration - player.currentTime <= value) {
        player.currentTime = duration;
      } else {
        player.currentTime += value;
      }
    },

    handleOnPressShowMore() {
      this.hashTagModalIsVisible = !this.hashTagModalIsVisible;
    },

    isMini() {
      return this.configs.mode === 'mini';
    },

    isClassic() {
      return this.configs.mode === 'classic';
    },

    handleSearchClick() {
      this.searchIsVisible = true;
      // Use only the nextTick promise (not the callback form) so vue/valid-next-tick
      // is satisfied; still resolves to undefined after the tick (Player-teardown.spec
      // pins that). `.then` + arrow is ES2015-safe (keeps the legacy lint green).
      return this.$nextTick().then(() => {
        this.focusSearchInput();
      });
    },

    focusSearchInput() {
      // v4 renders its search as an INLINE native <input ref="searchInput"> in the
      // "next up" header — there is NO <search> child component here (contrast v3,
      // which has <search ref="search"> exposing focusInput()). Focus that input
      // directly via its own template ref.
      //
      // This replaces the dead Vue-1 `this.$refs.search.$els.searchInput.focus()`
      // reach-through: `$refs.search` was always undefined (no such child) and
      // `.$els` is a removed Vue-1 idiom, so the old `&&` guard short-circuited to a
      // permanent no-op that never focused anything. That was the last `$els` code
      // reference in src/ (dead-`$els` audit / FOLD E).
      if (this.$refs.searchInput) {
        this.$refs.searchInput.focus();
      }
    },

    handleClearSearch() {
      this.showingFilteredPodcasts = false;
      this.searchTerm = '';
    },

    toggleAutoplay() {
      this.autoplayActive = !this.autoplayActive;
    },

    retrieveShowData() {
      // MR-49 (P5-7): hand-rolled XHR Promise -> shared getJson helper. getJson
      // reproduces this method's exact contract (200 -> parsed JSON; non-200 ->
      // Error(`${statusText} (${status})`)), so this is behaviour-preserving.
      return getJson(this.resourceUrlForShowName);
    },

    setShowTitle() {
      // this sets the show title for the player in the top half of the widget specifically
      if (!this.apiResourceUrl) return;

      this.retrieveShowData().then((res) => {
        this.showTitle = res.show_title;
      });
    },
  },
  mounted() {
    // Init the time remaining
    // Through metadataDuration() so a missing duration cannot render NaN:NaN on
    // first paint — raised three times by the fold review, and pre-existing.
    this.timeRemaining = this.metadataDuration();
    this.setShowTitle();
  },
  beforeUnmount() {
    // The Vue-1 original had no teardown: a swapped-out / torn-down player left
    // the <audio> playing and any live HLS session still attached. Stop
    // playback, then detach the live engine (hls.js, LP-H/MR-75).
    // detachLiveStream is null-safe + throw-safe (mirrors setupHlsEngine), so it
    // handles an absent $refs.player and the iOS quirk. (Was the guarded Viblast
    // stop call on the runtime global, migrated to hls.js.)
    this.pauseMedia();
    detachLiveStream(this.$refs.player);
  },
};
</script>

<style lang="scss" scoped>
@import '../assets/_helpers';

$screen-sm: 540px;

.widget-wrap {
  border-radius: 2px;
  max-height: 624px;

  // R5 follow-up: 624 is not arbitrary — it is the DESKTOP arithmetic
  // (`.player` 224 + `.full` 400). This element paints the widget's background,
  // and it sets no overflow, so once the content is taller than the cap the
  // excess renders BELOW the coloured box, on the host page, where the widget's
  // white-on-black text is invisible. R5's mobile stack makes the player 446px,
  // so at 320 the content runs to ~780 against a 624 background — measured 125px
  // of list rows spilling out. The stacked layout is simply taller than the
  // arithmetic this cap encodes, so drop the cap below the breakpoint (desktop
  // keeps it, and the 1280px oracle with it).
  @media (max-width: 480px) {
    max-height: none;
  }
}

.player {
  .menu-trigger {
    position: absolute;
    right: 10px;
    top: 10px;
    cursor: pointer;
    z-index: 9;

    // R11 (a11y): the "..." trigger renders 22x26 — well under the plan's 44px
    // touch-target figure (§7.4). Expand the HIT AREA only (transparent, out of
    // flow, pixel-identical). It sits alone in the corner, so a 44px box has no
    // neighbouring target to steal from — but it is only ~21px from the frame's
    // right edge, so the box is anchored RIGHT rather than centred: a centred one
    // would put a third of itself outside the document, where it is unreachable.
    // @media(max-width)-gated ONLY.
    @media (max-width: 480px) {
      &::before {
        content: '';
        position: absolute;
        top: 50%;
        right: 0;
        transform: translateY(-50%);
        width: 44px;
        height: 44px;
      }
    }
  }

  position: relative;
  display: flex;
  width: 100%;
  height: 224px;
  padding: 20px;
  align-items: stretch;
  font-family: "Roboto", sans-serif;

  .main-image {
    display: flex;
    align-items: center;
    position: relative;
    overflow: hidden;

    .main-desc {
      font-size: 12px;
      position: absolute;
      height: 100%;
      padding: 20px 14px;
      border-radius: 20px;
      left: 0;
      opacity: 0;
      line-height: 18px;
      overflow-y: auto;
      max-height: 100%;
      bottom: -100%;
      width: 100%;
      background-color: #333;
      color: white;
      transition: transform 0.5s ease, opacity 1s ease;
    }

    &:hover {
      .main-desc {
        opacity: 1;
        color: white;
        transform: translateY(-100%);
        background-color: rgba(0,0,0, 0.8);
      }
    }

    .image {
      width: 172px;
      height: 172px;
      border-radius: 20px;
    }
  }

  .image-placeholder {
    color: #7f7f7f;
    background: #242424;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  // R5 / #399 on v4 — mobile STACK + fluid artwork.
  // The base layout above is a fixed-height (224px) flex ROW holding a hard
  // 172x172 cover. `.main-image` carries `overflow: hidden`, which zeroes its
  // automatic minimum size, while `.content` keeps `min-width: auto` — so every
  // pixel of squeeze lands on the artwork. Measured on the built bundle: at a
  // 292px frame (320px viewport) the cover is clipped to a 10px sliver (3%), at
  // 328px to 46px (14%) — card #399's "artwork cut in half". The same squeeze
  // pins the player's min-content at 292px, so the iframe also overflows a
  // 288px host column by 4px.
  // Plan §7.2 (design call, Adrian 2026-07-24): below the mobile breakpoint the
  // single-episode player STACKS — artwork on top of the controls. The cover
  // goes fluid, capped at 240px to match the v1 #399 cap, with the same
  // max-height + object-fit:cover recipe so a non-square cover crops instead of
  // distorting (plan §7.4). Breakpoint, cap and paddings are tunable defaults;
  // the look is pending sign-off. @media(max-width)-gated ONLY, so the frozen
  // 1280px cross-cutover oracle is untouched.
  //
  // ⚠️ EXCEPT the `vertical=true` variant, which this "mobile-only" framing does
  // NOT cover. The media query is evaluated against the IFRAME's viewport, and
  // the vertical variant is pinned to a 320px iframe at EVERY host width
  // (contract register #3) — so (max-width: 480px) is unconditionally true for
  // it and these rules ship to DESKTOP vertical embeds too. Measured on a
  // 1280px host: column=1248, iframe=320, mq480=true. Accepted deliberately (a
  // 320px-wide widget wants the mobile shape whatever the host width), and
  // locked by the "vertical variant on a DESKTOP host" test in
  // test/e2e/responsive.spec.js — but it is a look change on desktop and needs
  // the same sign-off as the mobile one.
  @media (max-width: 480px) {
    flex-direction: column;
    height: auto;
    padding: 16px;

    .main-image {
      justify-content: center;
      // Reserve the box on the CONTAINER, not just the placeholder. When
      // `imageLoaded` flips, the placeholder is removed in the same render as the
      // real <img> appears — and that <img> has no intrinsic size until its
      // pixels paint, so the container COLLAPSES TO 0 for that window. Measured
      // on the deployed build with a 2s cover: .main-image went 240 -> 0 -> 240,
      // and a layout read landing inside the gap saw 0. The user sees the same
      // thing as a jump. Reserving here makes the swap invisible in both senses.
      min-height: 240px;

      .image {
        width: 100%;
        max-width: 240px;
        height: auto;
        max-height: 240px;
        object-fit: cover;
      }

      // `height: auto` above is right for a real <img> (it takes the cover's
      // intrinsic aspect) but WRONG for the placeholder div, which has no
      // intrinsic height and collapses to its ~19px text line box. Desktop gives
      // it an explicit 172x172; mobile gave it nothing.
      //
      // That is not only the loading phase. `imageError()` leaves
      // `imageLoaded === false` permanently, so a cover that 404s — live today on
      // zoneless embeds — leaves a 240x19px grey sliver reading "Image" where a
      // 240x240 cover belongs, as the STEADY state. No fixture ever fails to load
      // (all four are data-URI SVGs), so nothing could catch it.
      .image-placeholder {
        height: 240px;
        max-height: 240px;
      }
    }

    .content {
      min-width: 0;
      padding: 12px 0 0;
    }
  }

  &.vertical {
    flex-direction: column;
    height: 100%;
    padding: 0;


    .main-desc {
      font-size: 14px;
      position: absolute;
      padding: 24px;
      left: 0;
      opacity: 1;
      line-height: 20px;
      overflow-y: auto;
      max-height: 100%;
      border-radius: 0;
      bottom: -100%;
      background-color: #333;
      color: white;
      transition: transform 0.5s ease, opacity 1s ease;
    }

    .main-image {
      &:hover {
        .main-desc {
          opacity: 1;
          color: white;
          transform: translateY(-100%); /* Slide in */
          background-color: rgba(0, 0, 0, 0.9);
          border-radius: 0;
        }
      }
    }

    .content {
      padding: 0 20px 10px 20px;
    }

    h6 {
      color: white;
      margin-bottom: 7px;
      font-weight: bold;
      font-size: 17px;
      height: 40px;
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      white-space: normal;
    }

    .main-image {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 20px;

      .main-desc {
        font-size: 14px;
        position: absolute;
        padding: 24px;
        left: 0;
        opacity: 1;
        line-height: 20px;
        overflow-y: auto;
        max-height: 100%;
        border-radius: 0;
        bottom: -100%;/* Start off-screen at the bottom */
        background-color: #333; /* Adjust as needed */
        color: white;
        transition: transform 0.5s ease, opacity 1s ease;
      }

      &:hover {
        .main-desc {
          opacity: 1;
          color: white;
          transform: translateY(-100%); /* Slide in */
          background-color: rgba(0,0,0, 0.9);
          border-radius: 0;
        }

      }

      .image {
        width: 100%;
        height: 250px;
        border-radius: 20px;
        // The vertical iframe is pinned to 320px, so `(max-width: 480px)` is
        // UNCONDITIONALLY true for this variant — at every host width, desktop
        // included. That block sets max-width/max-height/object-fit on
        // `.player .main-image .image` (0,3,0); this selector is (0,4,0) and so
        // used to override only `height`, letting the other three through.
        // Measured at a 1280 desktop host: the hero cover rendered 240x240
        // center-CROPPED instead of its designed 280x250 — 40px (14%) of width
        // gone, with 20px background gutters either side. Restore the variant's
        // own geometry explicitly; `fill` is the pre-R5 behaviour this variant
        // was designed around.
        max-width: none;
        max-height: none;
        object-fit: fill;
      }
    }

    .menu-trigger {
      position: absolute;
      top: 360px;
      right: 20px;
    }
  }
}

.full {
  font-family: 'Inter', sans-serif;
  padding: 20px;
  max-height: 400px;
  display: flex;
  flex-direction: column;

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;

    .left-up {
      font-size: 18px;
    }

    .head-right {
      display: flex;
      align-items: center;
      gap: 15px;

      span {
        font-size: 14px;
      }
    }

    .search-input {
      border-radius: 4px;
      border-width: 1px;
      border-style: solid;
      background-color: transparent;
      padding: 4px 6px;

      &::placeholder {
        color: #bbb;
      }

      &:focus {
        outline: none;
        border: 1px solid #4CAF50;
      }
    }
  }

  // R6 — mobile list-panel header. Measured before this block, at a 292px frame:
  // `.head-right` (the 164px search input + gap + "N episodes") runs to x=292
  // inside a 252px header box, and because nothing clips it, THAT is what pinned
  // the whole document's min-content at 292px — the 4px iframe-over-column
  // overflow R0 carried as a fixme and R5 traced here. Let the header wrap and
  // let the search box shrink, so the group can never set a floor above the
  // column. Paddings/gaps are tunable defaults; @media(max-width)-gated ONLY.
  @media (max-width: 480px) {
    padding: 16px;

    .header {
      flex-wrap: wrap;
      gap: 8px;

      .head-right {
        flex: 1 1 auto;
        min-width: 0;
        gap: 8px;
      }

      .search-input {
        flex: 1 1 auto;
        min-width: 0;
        width: auto;
      }
    }
  }
}

.results {
  font-family: Roboto, sans-serif;
  overflow: auto;
  max-height: 100%;

  .result {
    display: flex;
    margin-bottom: 15px;
    align-items: center;
    justify-content: space-between;
    cursor: default;

    &:hover {
      .result-name {
        text-decoration: underline;
      }

    }

    &:hover {
      .left .image-wrap {

        span {
          opacity: 0;
          visibility: visible;
          transition: opacity 0.2s ease-in-out;
          position: absolute;
          left: 0;
          top: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 55px;
          height: 55px;
          background-color: rgba(0, 0, 0, 0.5);
          border-radius: 4px;
          background-blend-mode: multiply;

          &.play-icon {
            opacity: 1;
          }

          &:hover {
            opacity: 1;

            svg {
              transition: fill, transform 0.1s ease-in-out;
              transform: scale(1.1);
            }
          }
        }
      }
    }

    .left {
      display: flex;
      align-items: center;
      gap: 10px;

      .image-wrap {
        position: relative;
        width: 55px;
        height: 55px;
        cursor: pointer;

        span {
          opacity: 0;
          visibility: visible;
          transition: opacity 0.2s ease-in-out;
          position: absolute;
          left: 0;
          top: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 55px;
          height: 55px;
          background-color: rgba(0, 0, 0, 0.5);
          border-radius: 4px;
          background-blend-mode: multiply;

          &.play-icon.playing {
            opacity: 1;
          }

          &.pause-icon {
            opacity: 1;
          }

          &:hover {
            opacity: 1;

            svg {
              transition: fill, transform 0.1s ease-in-out;
              transform: scale(1.1);
            }
          }
        }
      }
    }

    .right {
      display: flex;
      align-items: center;
      gap: 3px;
      font-weight: 400;
      font-size: 13px;
    }

    .result-image {
      width: 55px;
      height: 55px;
      border-radius: 6px;
      object-fit: cover;
    }

    .result-name {
      font-size: 16px;
      font-weight: 600;
      max-width: 450px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  // R6 — mobile list rows. Measured before this block, at a 292px frame: every
  // row ran out to x=413 inside a 252px box. `.left` (55px cover + 10px gap +
  // the `white-space: nowrap` title) would not shrink below its 318px
  // min-content, pushing `.right` (date/duration) out behind it; the row only
  // looked intact because `.results` sets `overflow: auto`, so on a phone the
  // title and the WHOLE date/duration column sat behind a horizontal scrollbar.
  // Plan §7.2 says list rows reflow by SHRINKING, not stacking — so unlock the
  // flex children's automatic minimum size and let the `text-overflow: ellipsis`
  // that `.result-name` already carries finally do its job.
  // @media(max-width)-gated ONLY.
  @media (max-width: 480px) {
    .result {
      .left {
        min-width: 0;
      }

      .result-name {
        min-width: 0;
      }

      .right {
        flex: 0 0 auto;
      }
    }
  }
}

.content {
  position: relative;
  display: flex;
  flex: 1;
  padding: 10px 20px;
  flex-direction: column;
  color: white;

  h6 {
    color: white;
    margin-bottom: 5px;
    font-weight: bold;
    font-size: 17px;
  }

  .info {
    display: flex;
    gap: 5px;
    color: gray;
    margin-bottom: 32px;
    font-size: 14px;
  }
}
</style>

<style>
* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}
</style>
